import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import GET_COMBO_VISITA_VALOR, { adaptCombosVisitas } from "../../../../../../data/graphQL/queries/MisVisitas/getCombosVisitaValor";
import SAVE_VISITA_FONOAUDIOLOGO, { adaptAddVisitaDisfagiaFonoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaDisfagiaFonoaudiologo";

const COMBO_VISITA_TIPO_DIETA = 'TipoDieta';
const COMBO_VISITA_TIPO_REHAB = 'TipoRehabilitacion';

export default function ProgramaCPCDFono({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, combosVisita }) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const [comboTipoDietaOptions, setComboTipoDietaOptions] = useState([]);
  const [comboTipoRehabilitacionOptions, setComboTipoRehabilitacionOptions] = useState([]);

  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { deleteArchivo } = useDeleteArchivoVisita();

  const inputCombos = groups.flatMap((g) => g.inputs.filter((i) => !!i.comboName));
  
  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: "no-cache"
  });

	useEffect(() => {
		if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
	}, [dataCombosRI]);

  const [loadComboTipoDietaOptions, { data: dataTipoDieta }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

	useEffect(() => {
		if (!dataTipoDieta) return;
    setComboTipoDietaOptions(adaptCombosVisitas(dataTipoDieta));
	}, [dataTipoDieta]);  

  const [loadComboTipoRehabilitacionOptions, { data: dataTipoRehabilitacion }] = useLazyQuery(GET_COMBO_VISITA_VALOR, {
    fetchPolicy: "no-cache"
  });

	useEffect(() => {
		if (!dataTipoRehabilitacion) return;
    setComboTipoRehabilitacionOptions(adaptCombosVisitas(dataTipoRehabilitacion));
	}, [dataTipoRehabilitacion]);  

  const [save] = useMutation(SAVE_VISITA_FONOAUDIOLOGO);

  const formattedGroups = useMemo(() => {
    const deglucionId = comboTipoRehabilitacionOptions?.find((x) => x.displayName.toLowerCase().includes('deglución'))?.id;
    const disartriaId = comboTipoRehabilitacionOptions?.find((x) => x.displayName.toLowerCase().includes('disartria'))?.id;

    const isDeglucion = formData?.idComboVisitaValorTipoRehabilitacion === deglucionId;
    const isDisartria = formData?.idComboVisitaValorTipoRehabilitacion === disartriaId;

    const denticionOpts = combosRI?.idComboRIValorDenticion || [];
    const dentTotalId = denticionOpts.find((o) => o.displayName?.toLowerCase().includes("total"))?.id;
    const dentParcialId = denticionOpts.find((o) => o.displayName?.toLowerCase().includes("parcial"))?.id;
    const requiereProtesis = [dentTotalId, dentParcialId].includes(formData?.idComboRIValorDenticion);

    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (newInput.comboName) newInput.options = combosRI?.[input.name];

        switch (input.name) {
          case "idComboVisitaValorTipoRehabilitacion":
            newInput.options = comboTipoRehabilitacionOptions;
            break;
          case "videodeglucion":
            newInput.hidden = !isDeglucion;
            newInput.required = isDeglucion;
            break;

          case "informeVideodeglucion":
            newInput.hidden = !(isDeglucion && formData?.videodeglucion === true);
            newInput.required = isDeglucion && formData?.videodeglucion === true;
            break;

          case "examenClinico":
            newInput.hidden = !(isDeglucion && formData?.videodeglucion === false);
            newInput.required = isDeglucion && formData?.videodeglucion === false;
            break;

          case "actividadMotora":
          case "controlCefalico":
          case "idComboRIValorPostural":
          case "idComboRIValorDenticion":
            newInput.hidden = !isDeglucion;
            newInput.required = isDeglucion;
            break;

          case "idComboRIValorProtesis":
            newInput.hidden = !(isDeglucion && requiereProtesis);
            newInput.required = isDeglucion && requiereProtesis;
            break;

          case "nadaPorBoca":
            newInput.hidden = !isDeglucion;
            newInput.required = isDeglucion;
            break;

          case "justificaNadaPorBoca":
            newInput.hidden = !(isDeglucion && formData?.nadaPorBoca === true);
            newInput.required = isDeglucion && formData?.nadaPorBoca === true;
            break;

          case "idComboVisitaValorTipoDieta":
            newInput.hidden = !isDeglucion;
            newInput.required = isDeglucion;
            newInput.options = comboTipoDietaOptions;
            break;
          case "idComboRIValorHidratacion":
            newInput.hidden = !isDeglucion;
            newInput.required = isDeglucion;
            break;

          case "maniobrasPosturales":
            newInput.hidden = !isDeglucion;
            break;

          case "complicaciones":
            // Exclusividad "Ninguna"
            const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === "ninguna")?.id;
            newInput.options = complicacionesCombo?.map((opt) => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
                if (hasNinguna) return { ...opt, disabled: opt.id !== ningunaId };
                if (hasOther) return { ...opt, disabled: opt.id === ningunaId };
              }
              return { ...opt, disabled: false };
            });
            // visible en ambos modos
            newInput.hidden = !(isDeglucion || isDisartria);
            newInput.required = isDeglucion || isDisartria;
            break;

          case "indicacion":
            newInput.hidden = !isDisartria;
            break;

          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [
  readOnly,
  comboTipoRehabilitacionOptions,
  combosRI,
  comboTipoDietaOptions,
  formData?.idComboVisitaValorTipoRehabilitacion,
  formData?.idComboRIValorDenticion,
  formData?.videodeglucion,
  formData?.nadaPorBoca,
  formData.complicaciones,
  complicacionesCombo]
  );

  const handleChange = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.LABORATORIO
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({ idArchivo: file.idArchivo, name, handleChange });
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });

    const { data } = await save({ variables: { input: adaptAddVisitaDisfagiaFonoInput(formData) } });

    const idVisitaDisfagia = data?.saveVisitaDisfagiaFonoaudiologo?.idVisitaDisfagia;
    if (!idVisitaDisfagia) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaDisfagia }));
  });

  useEffect(() => {
    if (!Array.isArray(combosVisita) || combosVisita.length === 0) return;

    const tipoDietaId = combosVisita.find((c) => c.comboName === COMBO_VISITA_TIPO_DIETA)?.id;

    if (Number.isInteger(tipoDietaId)) {
      loadComboTipoDietaOptions({ variables: { idComboVisita: tipoDietaId } });
    }
  }, [combosVisita, loadComboTipoDietaOptions]);

  useEffect(() => {
    if (!Array.isArray(combosVisita) || combosVisita.length === 0) return;

    const tipoRehabId = combosVisita.find((c) => c.comboName === COMBO_VISITA_TIPO_REHAB)?.id;

    if (Number.isInteger(tipoRehabId)) {
      loadComboTipoRehabilitacionOptions({ variables: { idComboVisita: tipoRehabId } });
    }
  }, [combosVisita, loadComboTipoRehabilitacionOptions]);

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);
      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisita: visita.idVisita,
        idVisitaDisfagia: visita.idVisitaDisfagia
      });
    }
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "evolucionPacienteDIFono",
      stepName: "Evolución Paciente (DI) - Fono",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
            {formattedGroups.
      filter((group) => !group.hidden).
      map((group, idx) =>
      <CardBox key={idx} title={group.title}>
                        <Grid container spacing={2}>
                            {group.inputs.
          filter((input) => !input.hidden).
          map((input) =>
          <Grid size={{ xs: 12 }} key={input.name}>
                                        <RenderInputByTypeMobile
              input={input}
              value={formData[input.name]}
              handleChange={input.type === "file" ? handleChangeFile : handleChange}
              handleDelete={input.type === "file" ? onDeleteImage : null}
              row={input.type === "multiple_boolean"} />
            
                                    </Grid>
          )}
                        </Grid>
                    </CardBox>
      )}
        </>);

}

const groups = [
{
  title: "",
  inputs: [
  {
    name: "idComboVisitaValorTipoRehabilitacion",
    displayName: "Tipo de rehabilitación",
    type: "select",
    required: true
  },
  {
    name: "videodeglucion",
    displayName: "Videodeglución",
    type: "boolean",
    required: true,
    lightTitle: true
  },
  {
    name: "informeVideodeglucion",
    displayName: "Informe de Videodeglución",
    type: "file"
  },
  {
    name: "examenClinico",
    displayName: "Examen clínico de deglución",
    type: "long_string",
    maxLength: 1000
  },
  {
    name: "actividadMotora",
    displayName: "Actividad motora y postura corporal al momento de alimentarse",
    type: "boolean",
    lightTitle: true
  },
  {
    name: "controlCefalico",
    displayName: "Control cefálico",
    type: "boolean",
    lightTitle: true
  },
  {
    name: "idComboRIValorPostural",
    displayName: "Control postural",
    comboName: "Control postural",
    type: "select"
  },
  {
    name: "idComboRIValorDenticion",
    displayName: "Dentición",
    comboName: "Dentición",
    type: "select"
  },
  {
    name: "idComboRIValorProtesis",
    displayName: "Prótesis",
    comboName: "Prótesis",
    type: "select"
  },
  {
    name: "nadaPorBoca",
    displayName: "Nada por boca",
    type: "boolean",
    lightTitle: true
  },
  {
    name: "justificaNadaPorBoca",
    displayName: "Justificar si es SI",
    type: "long_string",
    maxLength: 1000
  },
  {
    name: "idComboVisitaValorTipoDieta",
    displayName: "Tipo de dieta",
    type: "select",
    options: []
  },
  {
    name: "idComboRIValorHidratacion",
    displayName: "Hidratación",
    comboName: 'INDICACIONES LIQUIDOS',
    type: "select"
  },
  {
    name: "maniobrasPosturales",
    displayName: "Maniobras posturales",
    type: "boolean",
    required: true,
    lightTitle: true
  },
  {
    name: "indicacion",
    displayName: "Indicación",
    required: true,
    type: "long_string",
    maxLength: 1500
  },
  {
    name: "complicaciones",
    displayName: "Complicaciones",
    type: "multiple_select",
    required: true
  }]

}];